import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkle, Spinner, TrendUp } from "@phosphor-icons/react";

const DIMENSION_HELP = {
  financial_structure: "Cap table clarity, cash discipline, debt/equity balance",
  business_model: "Repeatable revenue streams, unit economics, competitive moat",
  governance: "Board, decision-making processes, key-person risk",
  documentation: "Data-room quality, financials, contracts, KPIs traceable",
  growth_potential: "Market pull, expansion runway, defensibility",
};

export default function InvestmentReadiness() {
  const [dimensions, setDimensions] = useState([]);
  const [inputs, setInputs] = useState({});
  const [result, setResult] = useState(null);
  const [latest, setLatest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [d, l] = await Promise.all([
          api.get("/financial-health/dimensions"),
          api.get("/investment-readiness/latest"),
        ]);
        setDimensions(d.data.dimensions);
        const init = {};
        for (const dim of d.data.dimensions) init[dim.key] = l.data?.inputs?.[dim.key] ?? 50;
        setInputs(init);
        if (l.data && l.data.score != null) setLatest(l.data);
      } catch {
        toast.error("Failed to load readiness module");
      } finally { setLoading(false); }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/investment-readiness", inputs);
      setResult(data);
      setLatest(data);
      toast.success("Snapshot generated");
    } catch { toast.error("Snapshot generation failed"); }
    finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="p-8" data-testid="ir-loading"><Spinner size={20} weight="bold" className="animate-spin text-primary" /></div>;
  }

  const display = result || latest;

  return (
    <div className="p-6 sm:p-8 lg:p-12 max-w-5xl" data-testid="investment-readiness-page">
      <div className="text-overline text-primary">AI Intelligence · v1.1</div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight mt-2">Investment Readiness Snapshot</h1>
      <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
        Score yourself across 5 dimensions. Orixa computes a weighted 0–100 readiness index and suggests 3 concrete improvement actions.
      </p>

      <form onSubmit={submit} className="mt-8 bg-card border border-border rounded-2xl p-6 sm:p-8">
        <div className="space-y-6">
          {dimensions.map((d) => (
            <div key={d.key} data-testid={`ir-dim-${d.key}`}>
              <div className="flex items-baseline justify-between">
                <label className="text-sm font-semibold" htmlFor={`ir-${d.key}`}>
                  {d.label} <span className="text-xs text-muted-foreground font-mono">· weight {Math.round(d.weight * 100)}%</span>
                </label>
                <span className="font-mono text-lg">{inputs[d.key] ?? 0}</span>
              </div>
              <div className="text-xs text-muted-foreground mb-2">{DIMENSION_HELP[d.key]}</div>
              <input
                id={`ir-${d.key}`}
                type="range" min="0" max="100" step="5"
                value={inputs[d.key] ?? 50}
                onChange={(e) => setInputs({ ...inputs, [d.key]: Number(e.target.value) })}
                className="w-full accent-teal-600"
                data-testid={`ir-slider-${d.key}`}
              />
            </div>
          ))}
        </div>
        <button type="submit" disabled={submitting}
          className="mt-6 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 disabled:opacity-50"
          data-testid="ir-submit">
          {submitting ? <Spinner size={14} weight="bold" className="animate-spin" /> : <Sparkle size={14} weight="bold" />}
          {submitting ? "Computing…" : "Generate snapshot"}
        </button>
      </form>

      {display && display.score != null && (
        <div className="mt-8 space-y-6" data-testid="ir-result">
          <div className="bg-primary text-primary-foreground rounded-2xl p-8 flex flex-col sm:flex-row items-start gap-6 card-elev">
            <div className="text-center sm:text-left">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold opacity-80">Investment readiness</div>
              <div className="font-display text-6xl leading-none mt-2 data-large">{display.score}<span className="text-2xl opacity-70">/100</span></div>
            </div>
            <div className="text-sm leading-relaxed opacity-90 flex-1">{display.explanation || "No explanation yet."}</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {display.breakdown?.map((b) => (
              <div key={b.key} className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-semibold">{b.label}</div>
                  <div className="font-mono text-sm">{b.score}<span className="text-xs text-muted-foreground">/100</span></div>
                </div>
                <div className="mt-2 h-1.5 bg-secondary rounded-full overflow-hidden">
                  <div className="h-full bg-primary" style={{ width: `${b.score}%` }} />
                </div>
                <div className="text-xs text-muted-foreground font-mono mt-1">weight {Math.round(b.weight * 100)}% · contribution {b.contribution}</div>
              </div>
            ))}
          </div>

          {display.improvement_actions?.length > 0 && (
            <div className="bg-card border border-border rounded-2xl p-6" data-testid="ir-actions">
              <div className="inline-flex items-center gap-2 text-overline text-gold mb-3">
                <TrendUp size={12} weight="bold" /> Priority actions
              </div>
              <ol className="space-y-3">
                {display.improvement_actions.map((a, i) => (
                  <li key={`ir-a-${i}`} className="flex gap-3">
                    <div className="h-6 w-6 rounded-full bg-gold-soft text-gold grid place-items-center text-sm font-bold shrink-0">{i + 1}</div>
                    <div className="text-sm leading-relaxed">{a}</div>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
